import { createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createMagicDoorContext } from '~/contexts/utils';
import { RentPaymentProcessingFeesSettingsRepository } from '~/repositories/settings/rentPaymentProcessingFeesSettingsRepository';
import type { Level } from '~/repositories/settings/rentPaymentProcessingFeesSettingsRepository';
import type {
  RentPaymentProcessingFeesSettings,
  RentPaymentProcessingFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument,
  RentPaymentProcessingFeesSettingsTypedSettingsOverrideEntryDocument,
  SettingsLevel,
} from '~/swagger/settings/Api';

const repo = new RentPaymentProcessingFeesSettingsRepository();

export const [RentPaymentProcessingFeesSettingsProvider, useRentPaymentProcessingFeesSettings] = createMagicDoorContext(
  'RentPaymentProcessingFeesSettings',
  () => {
    const [allSettings, allSettingsActions] = createLazyResource(() => repo.getAllRentPaymentProcessingFees());

    const updateSettings = createMutation(async (payload: RentPaymentProcessingFeesSettings) => {
      const updated = await repo.createRentPaymentProcessingFees(payload);
      allSettingsActions.mutate(
        (prev) =>
          ({
            ...prev,
            settings: updated,
          }) as RentPaymentProcessingFeesSettingsCompanyPortalTypedSettingsWithOverridesDocument
      );
    });

    const deleteSettings = createMutation(async () => {
      await repo.deleteRentPaymentProcessingFees();
      allSettingsActions.mutate(undefined);
    });

    const updateLevelSettings = createMutation(async (level: Level, levelId: string, payload: RentPaymentProcessingFeesSettings) => {
      const updated = await repo.createLevelRentPaymentProcessingFees(level, levelId, payload);
      const override: RentPaymentProcessingFeesSettingsTypedSettingsOverrideEntryDocument = {
        level: level as SettingsLevel,
        levelId,
        settings: updated,
      };
      allSettingsActions.mutate((prev) => {
        if (prev == null) return;
        if (!prev.overrides?.some((x) => x.level === level && x.levelId === levelId)) {
          return { ...prev, overrides: [...(prev.overrides ?? []), override] };
        }
        return {
          ...prev,
          overrides: prev.overrides?.map((x) => (x.level === level && x.levelId === levelId ? override : x)),
        };
      });
    });

    const deleteLevelSettings = createMutation(async (level: Level, levelId: string) => {
      await repo.deleteLevelRentPaymentProcessingFees(level, levelId);
      allSettingsActions.mutate((prev) => {
        if (prev == null) return;
        return {
          ...prev,
          overrides: prev.overrides?.filter((x) => x.level !== level || x.levelId !== levelId),
        };
      });
    });

    return {
      get allSettings() {
        allSettingsActions.fetch();
        return allSettings;
      },
      updateLevelSettings,
      deleteLevelSettings,
      updateSettings,
      deleteSettings,
    };
  }
);
